'use client'

import type { CookiebotEventPublisherProps } from '@maestro/cookiebot-cookie-consent/cookiebot.types'

import { useCallback, useEffect } from 'react'

import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { type EventPayloadType, EventType } from '@maestro/data-layer'

export function CookiebotEventPublisher(props: CookiebotEventPublisherProps) {
  const { config } = props
  const { publish } = useEventBusContext()

  const focusConsentDialog = useCallback(() => {
    const dialog = document.querySelector<HTMLElement>('#CybotCookiebotDialog')
    dialog?.focus()
  }, [])

  useEffect(() => {
    if (!config.apiKey || typeof window === 'undefined') {
      return
    }

    const handleEvent = (e: Event) => {
      const cookieConsent = {
        necessary: !!window.Cookiebot?.consent?.necessary,
        preferences: !!window.Cookiebot?.consent?.preferences,
        marketing: !!window.Cookiebot?.consent?.marketing,
        statistics: !!window.Cookiebot?.consent?.statistics,
      }

      const payload = { cookieConsent }

      switch (e.type) {
        case 'CookiebotOnAccept':
          publish<EventPayloadType[EventType.COOKIE_CONSENT_GRANTED]>(
            EventType.COOKIE_CONSENT_GRANTED,
            payload
          )
          break
        case 'CookiebotOnDecline':
          publish<EventPayloadType[EventType.COOKIE_CONSENT_DECLINED]>(
            EventType.COOKIE_CONSENT_DECLINED,
            payload
          )
          break
        case 'CookiebotOnDialogDisplay':
          publish<EventPayloadType[EventType.COOKIE_CONSENT_DIALOG_OPENED]>(
            EventType.COOKIE_CONSENT_DIALOG_OPENED,
            payload
          )
          focusConsentDialog()
          break
        case 'CookiebotOnConsentReady':
          publish<EventPayloadType[EventType.COOKIE_CONSENT_SET]>(
            EventType.COOKIE_CONSENT_SET,
            payload
          )
          break
      }
    }

    // Register event listeners
    window.addEventListener('CookiebotOnAccept', handleEvent)
    window.addEventListener('CookiebotOnDecline', handleEvent)
    window.addEventListener('CookiebotOnDialogDisplay', handleEvent)
    window.addEventListener('CookiebotOnConsentReady', handleEvent)

    // Check if Cookiebot already fired CookiebotOnConsentReady before we added listeners
    // Poll for Cookiebot API to be ready (it starts as script element, then becomes API)
    const checkExistingConsent = () => {
      const cb = window.Cookiebot
      // Cookiebot API has 'consent' as an object, script element doesn't
      if (cb && typeof cb.consent === 'object' && cb.consent !== null) {
        publish<EventPayloadType[EventType.COOKIE_CONSENT_SET]>(
          EventType.COOKIE_CONSENT_SET,
          {
            cookieConsent: {
              necessary: !!cb.consent.necessary,
              preferences: !!cb.consent.preferences,
              marketing: !!cb.consent.marketing,
              statistics: !!cb.consent.statistics,
            },
          }
        )
        return true
      }
      return false
    }

    let pollingInterval: ReturnType<typeof setInterval> | null = null

    if (!checkExistingConsent()) {
      let attempts = 0
      pollingInterval = setInterval(() => {
        attempts++
        if (checkExistingConsent() || attempts >= 10) {
          clearInterval(pollingInterval!)
          pollingInterval = null
        }
      }, 200)
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval)
      }
      window.removeEventListener('CookiebotOnAccept', handleEvent)
      window.removeEventListener('CookiebotOnDecline', handleEvent)
      window.removeEventListener('CookiebotOnDialogDisplay', handleEvent)
      window.removeEventListener('CookiebotOnConsentReady', handleEvent)
    }
  }, [config.apiKey, focusConsentDialog, publish])

  return null
}
